import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Check, TrendingUp, Users, Hotel as HotelIcon, Receipt, IndianRupee, FileText, Tag, X, Save, Edit3, FilePenLine } from 'lucide-react';
import { MonthPicker } from '../components/MonthPicker';
import { AdminHotelEditor } from '../components/admin/AdminHotelEditor';
import { SiteContentEditor } from '../components/admin/SiteContentEditor';
import { DeployBanner } from '../components/admin/DeployBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const formatMonth = (m) => {
  if (!m) return '';
  const [y, mm] = m.split('-');
  const date = new Date(parseInt(y), parseInt(mm) - 1, 1);
  return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'long' });
};

export const AdminDashboard = () => {
  const [pendingHotels, setPendingHotels] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [monthFilter, setMonthFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [allHotels, setAllHotels] = useState([]);
  const [badgeEdits, setBadgeEdits] = useState({}); // { hotel_id: ['Eco', 'Heritage', ...] }
  const [badgeInput, setBadgeInput] = useState({}); // { hotel_id: 'currently typed string' }

  const fetchAllHotelsForBadges = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/hotels`, { withCredentials: true });
      setAllHotels(data);
      const seed = {};
      data.forEach((h) => { seed[h._id] = Array.isArray(h.badges) ? h.badges : []; });
      setBadgeEdits(seed);
    } catch {
      toast.error('Failed to load hotels');
    }
  }, []);

  const addBadge = (hotelId) => {
    const value = (badgeInput[hotelId] || '').trim();
    if (!value) return;
    setBadgeEdits((p) => {
      const cur = p[hotelId] || [];
      if (cur.length >= 4) {
        toast.error('Max 4 badges per hotel');
        return p;
      }
      if (cur.some((b) => b.toLowerCase() === value.toLowerCase())) return p;
      return { ...p, [hotelId]: [...cur, value.slice(0, 24)] };
    });
    setBadgeInput((p) => ({ ...p, [hotelId]: '' }));
  };

  const removeBadge = (hotelId, idx) => {
    setBadgeEdits((p) => ({ ...p, [hotelId]: (p[hotelId] || []).filter((_, i) => i !== idx) }));
  };

  const saveBadges = async (hotelId) => {
    try {
      await axios.put(
        `${API}/admin/hotels/${hotelId}/badges`,
        { badges: badgeEdits[hotelId] || [] },
        { withCredentials: true },
      );
      toast.success('Badges saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [hotelsRes, commissionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/hotels/pending`, { withCredentials: true }),
        axios.get(`${API}/admin/commissions`, { withCredentials: true }),
        axios.get(`${API}/admin/statistics`, { withCredentials: true }),
      ]);
      setPendingHotels(hotelsRes.data);
      setCommissions(commissionsRes.data);
      setStatistics(statsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const url = monthFilter
        ? `${API}/admin/invoices?month=${monthFilter}`
        : `${API}/admin/invoices`;
      const { data } = await axios.get(url, { withCredentials: true });
      setInvoices(data);
    } catch (error) {
      toast.error('Failed to load invoices');
    }
  }, [monthFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (tab === 'invoices') fetchInvoices();
    if (tab === 'badges') fetchAllHotelsForBadges();
  }, [tab, fetchInvoices, fetchAllHotelsForBadges]);

  const handleApproveHotel = async (hotelId) => {
    try {
      await axios.put(`${API}/admin/hotels/${hotelId}/approve`, {}, { withCredentials: true });
      toast.success('Hotel approved successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve hotel');
    }
  };

  const toggleInvoiceStatus = async (invoice) => {
    const isPaid = invoice.status === 'paid';
    const endpoint = isPaid ? 'mark-pending' : 'mark-paid';
    try {
      await axios.put(`${API}/admin/invoices/${invoice.hotel_id}/${invoice.month}/${endpoint}`, {}, { withCredentials: true });
      toast.success(isPaid ? 'Marked as pending' : 'Marked as paid');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    }
  };

  const totalRevenue = invoices.reduce((sum, i) => sum + (i.total_revenue || 0), 0);
  const totalCommission = invoices.reduce((sum, i) => sum + (i.total_commission || 0), 0);
  const paidCommission = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.total_commission, 0);
  const pendingCommission = totalCommission - paidCommission;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF0059]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 bg-slate-50" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6">
        <DeployBanner />
        <h1 className="text-4xl font-black tracking-tighter text-slate-900 mb-2">Admin Dashboard</h1>
        <p className="text-slate-600 mb-8">Manage hotels, monitor revenue & commission invoices</p>

        {/* Tabs */}
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1 mb-6 w-fit shadow-sm flex-wrap" data-testid="admin-tabs">
          {[
            { id: 'overview', label: 'Overview', icon: <TrendingUp className="h-4 w-4" /> },
            { id: 'approvals', label: 'Approvals', icon: <Check className="h-4 w-4" /> },
            { id: 'hotels', label: 'Edit Hotels', icon: <Edit3 className="h-4 w-4" /> },
            { id: 'invoices', label: 'Invoices', icon: <Receipt className="h-4 w-4" /> },
            { id: 'badges', label: 'Hotel Badges', icon: <Tag className="h-4 w-4" /> },
            { id: 'content', label: 'Site Content', icon: <FilePenLine className="h-4 w-4" /> },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-2 rounded-md font-semibold text-sm transition-all ${
                tab === t.id ? 'bg-[#FF0059] text-white shadow' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="stat-hotels">
                <div className="flex items-center gap-3 mb-2">
                  <HotelIcon className="h-5 w-5 text-[#FF0059]" />
                  <span className="text-sm text-slate-600">Total Hotels</span>
                </div>
                <div className="text-3xl font-black text-slate-900">{statistics.total_hotels || 0}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="stat-approved">
                <div className="flex items-center gap-3 mb-2">
                  <Check className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-slate-600">Approved</span>
                </div>
                <div className="text-3xl font-black text-slate-900">{statistics.approved_hotels || 0}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="stat-pending">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="h-5 w-5 text-yellow-600" />
                  <span className="text-sm text-slate-600">Pending</span>
                </div>
                <div className="text-3xl font-black text-slate-900">{statistics.pending_hotels || 0}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="stat-guests">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  <span className="text-sm text-slate-600">Total Guests</span>
                </div>
                <div className="text-3xl font-black text-slate-900">{statistics.total_guests || 0}</div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Commission Tracking</h2>
              {commissions.length === 0 ? (
                <p className="text-slate-600" data-testid="no-commissions">No hotels with bookings yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Hotel</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Rate</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Bookings</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.map((hotel) => (
                        <tr key={hotel._id} className="border-b border-slate-200" data-testid={`commission-${hotel._id}`}>
                          <td className="py-3 px-4 text-sm text-slate-900">{hotel.hotel_name}</td>
                          <td className="py-3 px-4 text-sm text-slate-900">{hotel.commission_rate}%</td>
                          <td className="py-3 px-4 text-sm text-slate-900">{hotel.total_bookings}</td>
                          <td className="py-3 px-4 text-sm text-slate-900">
                            {hotel.rating > 0 ? `${hotel.rating.toFixed(1)}/10 (${hotel.rating_count})` : 'No ratings'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {tab === 'approvals' && (
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Pending Hotel Approvals</h2>
            {pendingHotels.length === 0 ? (
              <p className="text-slate-600" data-testid="no-pending-hotels">No pending hotel approvals.</p>
            ) : (
              <div className="space-y-3">
                {pendingHotels.map((hotel) => (
                  <div key={hotel._id} className="border border-slate-200 rounded-lg p-4" data-testid={`pending-hotel-${hotel._id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{hotel.hotel_name}</h3>
                        <p className="text-sm text-slate-600">{hotel.hotel_address}</p>
                        <p className="text-sm text-slate-600">City/Area: {hotel.city_area}</p>
                        <p className="text-sm text-slate-600">Contact: {hotel.email}</p>
                      </div>
                      <Button
                        onClick={() => handleApproveHotel(hotel._id)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-hotel-${hotel._id}`}
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'hotels' && (
          <div data-testid="admin-hotels-edit-section">
            <AdminHotelEditor />
          </div>
        )}

        {tab === 'content' && (
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="admin-content-section">
            <SiteContentEditor />
          </div>
        )}

        {tab === 'invoices' && (
          <div data-testid="invoices-section">
            {/* Invoice KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="kpi-total-revenue">
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-1"><IndianRupee className="h-4 w-4" /> GMV</div>
                <div className="text-2xl font-black text-slate-900">{formatINR(totalRevenue)}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="kpi-total-commission">
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-1"><Receipt className="h-4 w-4" /> Total Commission</div>
                <div className="text-2xl font-black text-slate-900">{formatINR(totalCommission)}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="kpi-paid-commission">
                <div className="flex items-center gap-2 text-sm text-green-700 mb-1"><Check className="h-4 w-4" /> Paid</div>
                <div className="text-2xl font-black text-green-700">{formatINR(paidCommission)}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="kpi-pending-commission">
                <div className="flex items-center gap-2 text-sm text-[#FF0059] mb-1"><FileText className="h-4 w-4" /> Pending</div>
                <div className="text-2xl font-black text-[#FF0059]">{formatINR(pendingCommission)}</div>
              </div>
            </div>

            {/* Filter */}
            <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Filter by month</label>
                <MonthPicker
                  value={monthFilter}
                  onChange={setMonthFilter}
                  testId="month-filter-input"
                />
              </div>
              {monthFilter && (
                <Button variant="outline" onClick={() => setMonthFilter('')} data-testid="clear-month-filter">
                  Clear
                </Button>
              )}
              <div className="ml-auto text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{invoices.length}</span> invoice{invoices.length !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Invoices List */}
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              {invoices.length === 0 ? (
                <p className="text-slate-600 text-center py-12" data-testid="no-invoices">No invoices yet. Invoices are auto-generated from confirmed bookings.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-bold text-slate-700 uppercase">Hotel</th>
                        <th className="text-left py-3 px-4 text-xs font-bold text-slate-700 uppercase">Month</th>
                        <th className="text-right py-3 px-4 text-xs font-bold text-slate-700 uppercase">Bookings</th>
                        <th className="text-right py-3 px-4 text-xs font-bold text-slate-700 uppercase">Revenue</th>
                        <th className="text-right py-3 px-4 text-xs font-bold text-slate-700 uppercase">Commission</th>
                        <th className="text-center py-3 px-4 text-xs font-bold text-slate-700 uppercase">Status</th>
                        <th className="text-right py-3 px-4 text-xs font-bold text-slate-700 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`invoice-${inv.id}`}>
                          <td className="py-3 px-4 text-sm">
                            <div className="font-semibold text-slate-900">{inv.hotel_name}</div>
                            <div className="text-xs text-slate-500">{inv.hotel_city}</div>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-700">{formatMonth(inv.month)}</td>
                          <td className="py-3 px-4 text-sm text-right text-slate-900">{inv.total_bookings}</td>
                          <td className="py-3 px-4 text-sm text-right text-slate-900 font-semibold">{formatINR(inv.total_revenue)}</td>
                          <td className="py-3 px-4 text-sm text-right text-[#FF0059] font-bold">{formatINR(inv.total_commission)}</td>
                          <td className="py-3 px-4 text-sm text-center">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                                inv.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                              }`}
                              data-testid={`invoice-status-${inv.id}`}
                            >
                              {inv.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-right">
                            <Button
                              size="sm"
                              variant={inv.status === 'paid' ? 'outline' : 'default'}
                              className={inv.status === 'paid' ? '' : 'bg-[#FF0059] hover:bg-[#CC0047]'}
                              onClick={() => toggleInvoiceStatus(inv)}
                              data-testid={`toggle-invoice-${inv.id}`}
                            >
                              {inv.status === 'paid' ? 'Mark Pending' : 'Mark Paid'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'badges' && (
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="badges-panel">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="text-xl font-black tracking-tighter text-slate-900">Hotel Badges &amp; Tags</h2>
                <p className="text-sm text-slate-500 mt-1">Set up to 4 dynamic badges per hotel (e.g. <em>Eco Stay</em>, <em>Heritage</em>, <em>LCP Verified</em>). Visible to guests on listings.</p>
              </div>
              <span className="text-xs font-bold text-slate-400">{allHotels.length} hotels</span>
            </div>

            {allHotels.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-10">Loading hotels…</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {allHotels.map((h) => {
                  const badges = badgeEdits[h._id] || [];
                  return (
                    <div key={h._id} className="py-4 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-5" data-testid={`badge-row-${h._id}`}>
                      <div className="lg:w-64 min-w-0">
                        <p className="font-bold text-slate-900 truncate text-sm">{h.hotel_name}</p>
                        <p className="text-xs text-slate-500 truncate">{h.city_area || h.hotel_address}</p>
                      </div>
                      <div className="flex-1 flex flex-wrap items-center gap-2">
                        {badges.map((b, i) => (
                          <span
                            key={`${h._id}-${b}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FFE5EE] border border-[#FF0059]/30 text-[11px] font-bold text-[#CC0047]"
                            data-testid={`badge-chip-${h._id}-${i}`}
                          >
                            {b}
                            <button
                              type="button"
                              onClick={() => removeBadge(h._id, i)}
                              className="hover:bg-white rounded-full"
                              aria-label="Remove badge"
                              data-testid={`badge-remove-${h._id}-${i}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                        {badges.length < 4 && (
                          <input
                            type="text"
                            value={badgeInput[h._id] || ''}
                            onChange={(e) => setBadgeInput((p) => ({ ...p, [h._id]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); addBadge(h._id); }
                            }}
                            placeholder="Add badge + Enter"
                            maxLength={24}
                            className="px-3 py-1.5 rounded-full border border-dashed border-gray-300 focus:border-[#FF0059] focus:outline-none focus:ring-2 focus:ring-[#FFE5EE] text-xs font-medium w-44"
                            data-testid={`badge-input-${h._id}`}
                          />
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => saveBadges(h._id)}
                        className="bg-[#FF0059] hover:bg-[#CC0047] text-white text-xs font-bold rounded-full"
                        data-testid={`badge-save-${h._id}`}
                      >
                        <Save className="h-3 w-3 mr-1" /> Save
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
